import React, { useState, useEffect } from 'react';
import { Typography, Card, Alert } from 'antd';
import { QRCodeCanvas as QRCode } from 'qrcode.react';
import { getEvents } from '../services/eventsService';
import { getPlaces } from '../services/placesService';
import dayjs from 'dayjs';
import { getCurrentDestination } from '../services/destinationsService';

const { Title, Text } = Typography;

function Kiosk() {
  const [events, setEvents] = useState([]);
  const [places, setPlaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mapIndex, setMapIndex] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);
  const [city, setCity] = useState(null);
  const [center, setCenter] = useState({
    lat: 37.823,
    lon: -97.998,
  });

  const mapUrls = [
    `https://embed.windy.com/embed.html?type=map&location=coordinates&metricRain=in&metricTemp=°F&metricWind=mph&zoom=6&overlay=cloudss&level=surface&lat=${center.lat}&lon=${center.lon}&_r=${refreshKey}`,
    `https://embed.windy.com/embed.html?type=map&location=coordinates&metricRain=in&metricTemp=°F&metricWind=mph&zoom=9&overlay=cloudss&level=surface&lat=${center.lat}&lon=${center.lon}&_r=${refreshKey}`,
    `https://embed.windy.com/embed.html?type=map&location=coordinates&metricRain=in&metricTemp=°F&metricWind=mph&zoom=6&overlay=wind&level=surface&lat=${center.lat}&lon=${center.lon}&_r=${refreshKey}`,
    `https://embed.windy.com/embed.html?type=map&location=coordinates&metricRain=in&metricTemp=°F&metricWind=mph&zoom=9&overlay=wind&level=surface&lat=${center.lat}&lon=${center.lon}&_r=${refreshKey}`,
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [eventsData, placesData, cityData] = await Promise.all([
          getEvents(),
          getPlaces(),
          getCurrentDestination(),
        ]);

        const upcomingEvents = eventsData
          .filter(event => dayjs(event.start_date).isAfter(dayjs()))
          .sort((a, b) => dayjs(a.start_date).diff(dayjs(b.start_date)));

        setEvents(upcomingEvents);
        setPlaces(placesData);
        setCity(cityData);

        setCenter({
          lat: cityData.lat,
          lon: cityData.lon,
        });
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const mapTimer = setInterval(() => {
      setMapIndex(prev => (prev + 1) % mapUrls.length);
    }, 15000); // Change map view every 15 seconds

    return () => clearInterval(mapTimer);
  }, [mapUrls.length]);

  useEffect(() => {
    // Force the embeds to fetch fresh weather data every hour.
    const refreshTimer = setInterval(
      () => setRefreshKey(k => k + 1),
      60 * 60 * 1000,
    );
    return () => clearInterval(refreshTimer);
  }, []);

  useEffect(() => {
    // Kiosk safety net: reload every 3 hours to clear any accumulated
    // browser memory from long-running embeds.
    const reloadTimer = setTimeout(
      () => window.location.reload(),
      3 * 60 * 60 * 1000,
    );
    return () => clearTimeout(reloadTimer);
  }, []);

  if (loading) {
    return <></>;
  }

  if (error) {
    return (
      <div style={{ padding: '24px' }}>
        <Alert message={error} type="error" />
      </div>
    );
  }

  const baseEvents = events
    .filter(event => dayjs(event.start_date).isAfter(dayjs()))
    .slice(0, 20);

  const upcomingEvents = [...baseEvents, ...baseEvents, ...baseEvents, ...baseEvents];

  function changeScrollSpeed(speed) {
    const eventsContainer = document.getElementById('events-container');
    eventsContainer.style.animationDuration = `${speed}s`;
  }

  return (
    <div
      style={{
        height: '100vh',
        backgroundColor: 'black',
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        overflow: 'hidden',
      }}
    >
      <div style={{ flex: 1, overflowY: 'auto', marginRight: '24px', zIndex: 2 }}>
        <div id="events-container">
          {upcomingEvents.map((event, index) => (
            <div key={event.id || index} style={{ marginBottom: '16px', width: '100%' }}>
              <Card
                cover={
                  <div
                    style={{
                      position: 'relative',
                      borderRadius: 12,
                      overflow: 'hidden',
                    }}
                  >
                    <img
                      alt={event.name}
                      src={event.image_url || 'https://source.unsplash.com/random?event'}
                      style={{ height: 200, width: '100%', objectFit: 'cover' }}
                    />
                    <div
                      style={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        background: 'linear-gradient(transparent, rgba(0,0,0,0.8))',
                        padding: '20px',
                        color: 'white',
                      }}
                    >
                      <Title level={5} style={{ color: 'white', margin: 0 }}>
                        {event.name}
                      </Title>
                      <Text style={{ color: 'white' }}>
                        {dayjs(event.start_date).format('MMM D [at] h:mm A')}
                      </Text>
                    </div>
                  </div>
                }
                bodyStyle={{ display: 'none' }}
                style={{
                  height: '200px',
                  width: '100%',
                  cursor: 'pointer',
                }}
                onClick={() => window.open(event.ticket_url, '_blank')}
              />
            </div>
          ))}
        </div>
      </div>
      <div
        style={{
          flex: 3,
          height: 'calc(100vh + 200px)',
          overflow: 'hidden',
          margin: -44,
        }}
      >
        {' '}
        {city && (
          <div
            style={{
              position: 'absolute',
              top: 12,
              left: 'calc(25%  + 34px)',
              right: 24,
              zIndex: 5,
              backgroundColor: 'rgba(0,0,0,.5)',
              borderRadius: 12,
              backgroundImage: `url(${city.image_url})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              height: 200,
              overflow: 'hidden',
              boxShadow: '0 0 12px rgba(0,0,0,0.5)',
            }}
          >
            <div
              style={{
                height: '100%',
                width: '100%',
                padding: 24,
                background: 'linear-gradient(transparent, rgba(0,0,0,0.8))',
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  bottom: 12,
                }}
              >
                <Title
                  level={1}
                  style={{
                    color: 'white',
                    margin: 0,
                    fontWeight: 900,
                  }}
                >
                  {city.name}
                </Title>
                <Text style={{ color: 'white', fontSize: 22, fontWeight: 500 }}>
                  {city.description}
                </Text>
              </div>
            </div>
          </div>
        )}
        <div style={{ position: 'relative', width: '100%', height: '100%' }}>
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              bottom: 0,
              width: '50px',
              background: 'linear-gradient(to right, black, transparent)',
              zIndex: 1,
            }}
          />
          {mapUrls.map((url, index) => (
            <iframe
              key={index}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                zIndex: 0,
                opacity: index === mapIndex ? 1 : 0,
                transition: 'opacity 0.5s',
                pointerEvents: index === mapIndex ? 'auto' : 'none',
              }}
              src={url}
              frameBorder="0"
              title={`Interactive Map ${index + 1}`}
            ></iframe>
          ))}
        </div>
      </div>
      <div
        style={{
          position: 'fixed',
          bottom: '10px',
          right: '12px',
          zIndex: 10,
          backgroundColor: 'white',
          padding: '10px',
          borderRadius: '8px',
          boxShadow: '0 0 10px rgba(0,0,0,0.5)',
        }}
      >
        <QRCode value={'https://' + window.location.host + '/app'} size={120} />
      </div>
    </div>
  );
}

export default Kiosk;
